import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'

import { useRoomStores } from '../stores/useRoomStores'
import useConvexSync from '../hooks/useConvexSync'
import useDebouncedSearch from '../hooks/useDebouncedSearch'
import useRoomStore from '../stores/roomStore'
import useSearchStore from '../stores/searchStore'
import { useKeyboardHeight } from '../hooks/useKeyboardHeight'
import '../styles/participant-room.css'

// Import new components
import RoomHeader from '../components/RoomHeader'
import QueueSection from '../components/QueueSection'
import SearchModal from '../components/SearchModal'
import BottomActionBar from '../components/BottomActionBar'
import RoomStateGate from '../components/room/RoomStateGate'
import SyncLockoutModal from '../components/room/SyncLockoutModal'
import FirstTimeTutorialSheet from '../components/room/FirstTimeTutorialSheet'

// Import utility functions
import {
  isYouTubeUrl,
  getActiveQueue
} from '../utils/roomHelpers'

const ROOM_TUTORIAL_COMPLETED_KEY = 'karaoke_room_tutorial_completed_v1'
const ROOM_TUTORIAL_DISMISSED_KEY = 'karaoke_room_tutorial_dismissed_v1'
const SYNC_LOCKOUT_TIMEOUT_MS = 45000

const ParticipantPage = () => {
  const { roomCode } = useParams()
  const {
    room,
    queue,
    searchResults,
    isSearching,
    youtubeSearchResults,
    isSearchingYouTube,
    hasSearchedYouTube,
    processingJobs,
    isConnected,
    currentSong,
    pitchShift,
    joinRoom,
    searchSongs,
    searchYouTube,
    addToQueue,
    removeFromQueue,
    removeFromProcessing,
    reorderQueue,
    submitYouTubeUrl,
    loadSongs,
    getFilteredJobs,
    jobsFilter,
    setJobsFilter,
    getJobsStats,
    retryProcessingJob,
    jobsLoading,
    adjustPitch,
    sendEmojiReaction,
    authoritativeIsPlaying,
    authoritativePosSeconds,
    authoritativeAnchorPosSec,
    authoritativeAnchorServerMs
  } = useRoomStores()

  const isDarkMode = true

  const [inputQuery, setInputQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sessionId] = useState(() => {
    // Generate a unique session ID once per component mount
    return 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36)
  })
  const [activeTab, setActiveTab] = useState('search') // Only 'search' tab now
  const [queueTab, setQueueTab] = useState('queue') // 'queue' or 'history'
  const [isTutorialOpen, setIsTutorialOpen] = useState(false)
  const [showSyncLockoutModal, setShowSyncLockoutModal] = useState(false)

  // Search modal state
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)
  const [isSearchModalAnimating, setIsSearchModalAnimating] = useState(false)
  const disconnectedAtRef = useRef(null)
  const waitingCount = useMemo(
    () => queue.filter((item) => item.status === 'queued').length,
    [queue]
  )

  const getKeyDisplay = useCallback(() => {
    if (!currentSong || !currentSong.originalKey) return ''

    const keys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
    const originalIndex = keys.indexOf(currentSong.originalKey)

    if (originalIndex === -1) {
      return currentSong.originalKey
    }

    const shiftedIndex = (originalIndex + pitchShift + 12) % 12
    return keys[shiftedIndex]
  }, [currentSong, pitchShift])

  const handlePitchShiftChange = useCallback(async (newPitchShift) => {
    const clampedPitch = Math.max(-12, Math.min(12, newPitchShift))

    if (clampedPitch === pitchShift) {
      return
    }

    try {
      await adjustPitch(clampedPitch)
    } catch (error) {
      console.error('Failed to adjust pitch:', error)
    }
  }, [adjustPitch, pitchShift])

  const handleSendEmoji = useCallback(async (emojiToSend) => {
    try {
      await sendEmojiReaction(emojiToSend)
    } catch (error) {
      console.error('Failed to send emoji reaction:', error)
      toast.error('Unable to send reaction')
    }
  }, [sendEmojiReaction])

  // Initialize keyboard height detection
  useKeyboardHeight()

  const initializationRef = useRef(false)

  // Cache management refs for tracking previous state
  const previousQueueStateRef = useRef(null)

  // Convex real-time sync (replaces manual connectRealtime + loadProcessingJobs)
  const { isLoading: convexLoading, roomNotFound } = useConvexSync(roomCode)
  const queueActionsAvailable = isConnected && !convexLoading && !roomNotFound

  const handleRetryRoomJoin = useCallback(() => {
    joinRoom(roomCode)
  }, [joinRoom, roomCode])

  const handleManualResync = useCallback(() => {
    handleRetryRoomJoin()
    toast.success('Resync requested')
  }, [handleRetryRoomJoin])

  const handleRefreshAfterSyncLockout = useCallback(() => {
    window.location.reload()
  }, [])

  // Initialize room connection
  useEffect(() => {
    console.log('🚀 Initializing room:', roomCode)
    joinRoom(roomCode)
    loadSongs()

    return () => {
      console.log('🧹 Cleaning up room connection')
      useRoomStore.getState().disconnectRealtime()
    }
  }, [roomCode])


  // Modal animation helpers
  const openSearchModal = useCallback(() => {
    setIsSearchModalOpen(true)
    // Small delay to ensure the modal is in the DOM before starting animation
    setTimeout(() => setIsSearchModalAnimating(true), 10)
  }, [])

  const closeSearchModal = useCallback(() => {
    setIsSearchModalAnimating(false)
    // Wait for animation to complete before removing from DOM
    setTimeout(() => setIsSearchModalOpen(false), 300)
  }, [])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isSearchModalOpen) {
      // Save original overflow style
      const originalOverflow = document.body.style.overflow
      // Prevent scroll
      document.body.style.overflow = 'hidden'

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isSearchModalOpen])

  // Typing searches Convex only; YouTube remains an explicit action.
  useDebouncedSearch(
    inputQuery,
    Boolean(inputQuery.trim()) && !isYouTubeUrl(inputQuery),
    searchSongs
  )

  useEffect(() => {
    if (inputQuery.trim() && !isYouTubeUrl(inputQuery)) {
      // Reset YouTube search state when query changes but before search button is clicked
      const { resetYouTubeSearch } = useSearchStore.getState()
      resetYouTubeSearch()
    } else if (!inputQuery.trim()) {
      // Clear all results when input is cleared
      const { resetYouTubeSearch } = useSearchStore.getState()
      resetYouTubeSearch()
    }
  }, [inputQuery])

  // Handle search button click from QueueSection - just opens modal
  const handleQueueSearchClick = () => {
    openSearchModal()
  }

  // Handle manual search when search button is pressed in SearchModal
  const handleSearchSubmit = async () => {
    if (inputQuery.trim() && !isYouTubeUrl(inputQuery)) {
      // DB search is debounced separately; only trigger YouTube search here
      await searchYouTube(inputQuery)
    }

    // Always open modal when search button is clicked
    openSearchModal()
  }

  const handleAddToQueue = async (songId) => {
    try {
      await addToQueue(songId, sessionId)
    } catch (error) {
      console.error('Failed to add song to queue:', error)
    }
  }

  const handleRequeue = async (songId) => {
    try {
      await addToQueue(songId, sessionId)
    } catch (error) {
      console.error('Failed to re-queue song:', error)
    }
  }

  const handleRemoveFromQueue = async (itemId) => {
    if (!queueActionsAvailable) {
      toast.error('Queue actions are temporarily unavailable')
      return
    }

    try {
      await removeFromQueue(itemId)
    } catch (error) {
      console.error('Failed to remove song from queue:', error)
    }
  }

  const handleRemoveFromProcessing = async (jobId) => {
    try {
      await removeFromProcessing(jobId)
    } catch (error) {
      console.error('Failed to remove processing job:', error)
    }
  }

  const handleQueueReorder = async (orderedQueuedIds) => {
    if (!queueActionsAvailable) {
      toast.error('Queue actions are temporarily unavailable')
      return
    }

    if (!Array.isArray(orderedQueuedIds) || orderedQueuedIds.length === 0) {
      return
    }

    const activeQueue = getActiveQueue(queue)
    const queuedItems = activeQueue.filter(item => item.status !== 'playing')
    const queuedById = new Map(queuedItems.map((item) => [item.id.toString(), item]))
    const reorderedQueue = orderedQueuedIds
      .map((id) => queuedById.get(id.toString()))
      .filter(Boolean)

    if (reorderedQueue.length !== queuedItems.length) {
      return
    }

    const currentMinPosition = Math.min(...queuedItems.map(item => item.position))
    const basePosition = Number.isFinite(currentMinPosition) ? currentMinPosition : 0

    const updatedItems = reorderedQueue.map((item, index) => ({
      id: item.id,
      position: basePosition + index
    }))

    try {
      await reorderQueue(updatedItems)
    } catch (error) {
      console.error('Failed to reorder queue:', error)
      toast.error('Failed to reorder queue')
    }
  }

  const handleSubmitInput = async (e) => {
    e.preventDefault()

    if (!inputQuery.trim()) {
      toast.error('Please enter a search term or YouTube URL')
      return
    }

    if (isYouTubeUrl(inputQuery)) {
      // Handle YouTube URL submission
      setIsSubmitting(true)
      try {
        await submitYouTubeUrl(inputQuery, sessionId)
        setInputQuery('')
      } catch (error) {
        console.error('Failed to submit YouTube URL:', error)

        // Check if the error is specifically about song already existing
        const status = error.response?.status
        const detail = error.response?.data?.detail || error.message || ''

        if (status === 409) {
          if (detail.toLowerCase().includes('already exists')) {
            toast.error('🎵 Song already added to library')
          } else {
            toast.error(detail || 'Song already exists')
          }
        } else if (status === 429) {
          // Rate limit error - show the custom message
          toast.error(detail || 'Rate limit exceeded')
        } else if (error.isNetworkError) {
          toast.error('Network error - please check your connection')
        } else if (detail) {
          toast.error(detail)
        } else {
          toast.error('Failed to submit song. Please try again.')
        }
      } finally {
        setIsSubmitting(false)
      }
    } else {
      // Handle search: trigger YouTube search on Enter
      handleSearchSubmit()
    }
  }




  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const tutorialCompleted = window.localStorage.getItem(ROOM_TUTORIAL_COMPLETED_KEY) === 'true'
    const tutorialDismissed = window.localStorage.getItem(ROOM_TUTORIAL_DISMISSED_KEY) === 'true'

    if (!tutorialCompleted && !tutorialDismissed) {
      setIsTutorialOpen(true)
    }
  }, [])

  useEffect(() => {
    if (convexLoading || roomNotFound) {
      setShowSyncLockoutModal(false)
      disconnectedAtRef.current = null
      return
    }

    if (isConnected) {
      setShowSyncLockoutModal(false)
      disconnectedAtRef.current = null
      return
    }

    if (!disconnectedAtRef.current) {
      disconnectedAtRef.current = Date.now()
    }

    const interval = window.setInterval(() => {
      const disconnectedFor = Date.now() - (disconnectedAtRef.current || Date.now())
      if (disconnectedFor >= SYNC_LOCKOUT_TIMEOUT_MS) {
        setShowSyncLockoutModal(true)
      }
    }, 1000)

    return () => window.clearInterval(interval)
  }, [convexLoading, roomNotFound, isConnected])

  if (convexLoading || roomNotFound) {
    return (
      <RoomStateGate
        isLoading={convexLoading}
        roomNotFound={roomNotFound}
        roomCode={roomCode}
        onRetry={handleRetryRoomJoin}
      />
    )
  }

  return (
    <div className="participant-room-theme relative h-[100dvh] overflow-hidden">
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        {/* Room header, now playing, and queue share one scroll container. */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-style">
          <RoomHeader
            roomCode={roomCode}
            isDarkMode={isDarkMode}
            isConnected={isConnected}
            currentSong={currentSong}
            waitingCount={waitingCount}
            queueActionsAvailable={queueActionsAvailable}
            authoritativeIsPlaying={authoritativeIsPlaying}
            authoritativePosSeconds={authoritativePosSeconds}
            authoritativeAnchorPosSec={authoritativeAnchorPosSec}
            authoritativeAnchorServerMs={authoritativeAnchorServerMs}
            onOpenTutorial={() => setIsTutorialOpen(true)}
            onManualResync={handleManualResync}
          />

          <div className="room-content-safe-inline mx-auto flex min-h-full w-full max-w-lg flex-col gap-6 pb-24 pt-4">
            {/* Current Queue with Tabs (now includes processing jobs inline) */}
            <QueueSection
              queue={queue}
              queueTab={queueTab}
              setQueueTab={setQueueTab}
              waitingCount={waitingCount}
              isDarkMode={isDarkMode}
              handleRemoveFromQueue={handleRemoveFromQueue}
              handleRemoveFromProcessing={handleRemoveFromProcessing}
              handleRetryProcessingJob={retryProcessingJob}
              handleQueueReorder={handleQueueReorder}
              processingJobs={processingJobs}
              queueActionsAvailable={queueActionsAvailable}
              onRequeue={handleRequeue}
              authoritativeIsPlaying={authoritativeIsPlaying}
              authoritativePosSeconds={authoritativePosSeconds}
              authoritativeAnchorPosSec={authoritativeAnchorPosSec}
              authoritativeAnchorServerMs={authoritativeAnchorServerMs}
            />
            {/* Spacer for bottom action bar */}
            <div className="h-16" />
          </div>
        </div>
      </div>

      <BottomActionBar
        onSearchClick={handleQueueSearchClick}
        pitchShift={pitchShift}
        onPitchShiftChange={handlePitchShiftChange}
        getKeyDisplay={getKeyDisplay}
        onSendEmoji={handleSendEmoji}
      />

      {/* Search Modal */}
      <SearchModal
        inputQuery={inputQuery}
        setInputQuery={setInputQuery}
        isSearchModalOpen={isSearchModalOpen}
        isSearchModalAnimating={isSearchModalAnimating}
        closeSearchModal={closeSearchModal}
        isSubmitting={isSubmitting}
        handleSubmitInput={handleSubmitInput}
        handleSearchSubmit={handleSearchSubmit}
        // Search results
        isSearching={isSearching}
        searchResults={searchResults}
        handleAddToQueue={handleAddToQueue}
        queue={queue}
        processingJobs={processingJobs}
        sessionId={sessionId}
        // YouTube results
        isSearchingYouTube={isSearchingYouTube}
        youtubeSearchResults={youtubeSearchResults}
        hasSearchedYouTube={hasSearchedYouTube}
        submitYouTubeUrl={submitYouTubeUrl}
      />

      <SyncLockoutModal
        isOpen={showSyncLockoutModal}
        onRefresh={handleRefreshAfterSyncLockout}
      />

      <FirstTimeTutorialSheet
        isOpen={isTutorialOpen}
        onClose={() => {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(ROOM_TUTORIAL_DISMISSED_KEY, 'true')
          }
          setIsTutorialOpen(false)
        }}
        onComplete={() => {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(ROOM_TUTORIAL_COMPLETED_KEY, 'true')
            window.localStorage.removeItem(ROOM_TUTORIAL_DISMISSED_KEY)
          }
          setIsTutorialOpen(false)
        }}
      />
    </div>
  )
}

export default ParticipantPage
